import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Card, CardBody, CardTitle, Progress, Tooltip } from "reactstrap";
import { AudienceData } from "store/campaigns/types";

export interface CostEstimate {
	emailCost: number;
	smsCost: number;
	inAppCost: number;
	totalCost: number;
	whatsappCost?: number;
}

export interface StepCostProps {
	data: CostEstimate;
	setData: (c: CostEstimate) => void;
	channels: string[];     // ["EMAIL", "SMS", "IN_APP", "WHATSAPP"]
	audience: AudienceData;
}

const EMAIL_RATE = 0.01;
const SMS_RATE = 0.005;
const INAPP_RATE = 0.0;
const WHATSAPP_RATE = 0.02;

const StepCost: React.FC<StepCostProps> = ({
	data,
	setData,
	channels,
	audience,
}) => {
	const [tips, setTips] = useState<Record<string, boolean>>({
		email: false,
		sms: false,
		inApp: false,
		whatsapp: false,
	});
	const toggleTip = (k: keyof typeof tips) =>
		setTips((t) => ({ ...t, [k]: !t[k] }));

	// ---- Only update parent if the value has changed ----
	const prevEstimate = useRef<CostEstimate | null>(null);

	useEffect(() => {
		let recipientCount = 0;
		if (audience.type === "STATIC") {
			recipientCount = audience.imported_count || 0;
		} else {
			recipientCount = audience.estimated_count || 0;
		}
		const emailCost = channels.includes("EMAIL") ? EMAIL_RATE * recipientCount : 0;
		const smsCost = channels.includes("SMS") ? SMS_RATE * recipientCount : 0;
		const inAppCost = channels.includes("IN_APP") ? INAPP_RATE * recipientCount : 0;
		const whatsappCost = channels.includes("WHATSAPP") ? WHATSAPP_RATE * recipientCount : 0;
		const totalCost = emailCost + smsCost + inAppCost + whatsappCost;

		const newEstimate: CostEstimate = {
			emailCost,
			smsCost,
			inAppCost,
			whatsappCost,
			totalCost,
		};

		// Only update if different to prevent infinite loop
		const prev = prevEstimate.current;
		if (
			!prev ||
			prev.emailCost !== emailCost ||
			prev.smsCost !== smsCost ||
			prev.inAppCost !== inAppCost ||
			prev.whatsappCost !== whatsappCost ||
			prev.totalCost !== totalCost
		) {
			prevEstimate.current = newEstimate;
			setData(newEstimate);
		}
	}, [channels.join(","), audience.imported_count, audience.estimated_count, setData]); // narrow deps to only values that matter

	const { emailCost, smsCost, inAppCost, whatsappCost = 0, totalCost } = data;
	const pct = (c: number) => totalCost ? Math.round((c / totalCost) * 100) : 0;

	const channelConfig = [
		{
			key: "email",
			icon: "ri-mail-line",
			label: "Email",
			value: emailCost,
			info: `${EMAIL_RATE} USD × ${audience.type === "STATIC" ? (audience.imported_count || 0) : (audience.estimated_count || 0)} recipients`,
			color: "primary",
			enabled: channels.includes("EMAIL"),
		},
		{
			key: "sms",
			icon: "ri-message-2-line",
			label: "SMS",
			value: smsCost,
			info: `${SMS_RATE} USD × ${audience.type === "STATIC" ? (audience.imported_count || 0) : (audience.estimated_count || 0)} recipients`,
			color: "success",
			enabled: channels.includes("SMS"),
		},
		{
			key: "whatsapp",
			icon: "ri-whatsapp-line",
			label: "WhatsApp",
			value: whatsappCost,
			info: `${WHATSAPP_RATE} USD × ${audience.type === "STATIC" ? (audience.imported_count || 0) : (audience.estimated_count || 0)} recipients`,
			color: "success",
			enabled: channels.includes("WHATSAPP"),
		},
		{
			key: "inApp",
			icon: "ri-smartphone-line",
			label: "In-App",
			value: inAppCost,
			info: "No charge",
			color: "warning",
			enabled: channels.includes("IN_APP"),
		},
		{
			key: "total",
			icon: "ri-equal-line",
			label: "Total",
			value: totalCost,
			info: "Sum of all channels",
			color: "secondary",
			enabled: true,
		},
	];

	return (
		<>
			<Row className="mb-4">
				<Col>
					<h4 className="fw-bold">Campaign Cost Estimate</h4>
					<p className="text-muted">
						Here’s a breakdown by channel. Hover over each icon to learn more.<br />
						<strong>{audience.type === "STATIC" ? (audience.imported_count || 0) : (audience.estimated_count || 0)}</strong> recipients
					</p>
				</Col>
			</Row>
			<Row className="mb-5">
				<Col>
					<Progress multi style={{ height: 24, borderRadius: 12 }}>
						{channelConfig.filter(c => c.enabled && c.key !== "total").map((c, idx, arr) => (
							<Progress
								key={c.key}
								bar
								color={c.color}
								value={pct(c.value)}
								style={{
									borderTopLeftRadius: idx === 0 ? 12 : 0,
									borderBottomLeftRadius: idx === 0 ? 12 : 0,
									borderTopRightRadius: idx === arr.length - 1 ? 12 : 0,
									borderBottomRightRadius: idx === arr.length - 1 ? 12 : 0,
								}}
							>
								{pct(c.value)}% {c.label}
							</Progress>
						))}
					</Progress>
				</Col>
			</Row>
			<Row className="gx-4 mb-5">
				{channelConfig.filter(c => c.enabled || c.key === "total").map(({ key, icon, label, value, info, color }) => (
					<Col key={key} md={3} sm={6} xs={12}>
						<Card className="shadow-sm text-center">
							<CardBody>
								<i
									id={`tip-${key}`}
									className={`${icon} text-${color} fs-2 mb-2`}
									style={{ cursor: "pointer" }}
									onMouseEnter={() => toggleTip(key as any)}
									onMouseLeave={() => toggleTip(key as any)}
								/>
								<Tooltip
									target={`tip-${key}`}
									isOpen={tips[key as keyof typeof tips]}
									placement="top"
								>
									{info}
								</Tooltip>
								<CardTitle tag="h6" className="mb-2">
									{label}
								</CardTitle>
								<div className="fs-5 fw-semibold">
									${value.toFixed(2)}
								</div>
							</CardBody>
						</Card>
					</Col>
				))}
			</Row>
		</>
	);
};

export default StepCost;
